import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    DataGridPremium,
    GridSortModel,
    GridColDef,
    GridValueFormatter,
    GridPaginationModel,
} from '@mui/x-data-grid-premium';
import { Contact, Organization } from '../../utils';
import { AxiosResponse } from 'axios';
import { Request } from '../../api';
import { getIndividuals } from '../../api';
import { requestPodTransferToAthlete } from '../../api/Organization/requestPodTransferToAthlete';
import { styled } from '@mui/system';
import { debounce } from 'lodash';
import { cancelPodTransferToAthlete } from '../../api/Organization/cancelPodTransferToAthlete';
import { Pod } from '../../modules/admin/modules/pods/pods.types';
import PersonIcon from '@mui/icons-material/Person';
import { assignPodToAthlete } from '../../api/Organization/assignPodToAthlete';
import { removeAthleteFromPod } from '../../api/Organization/removeAthleteFromPod';
import CheckIcon from '@mui/icons-material/Check';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import InventoryIcon from '@mui/icons-material/Inventory';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { GridOverlay } from '@mui/x-data-grid-premium';

const StyledDataGrid = styled(DataGridPremium)({
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
        border: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus': {
        outline: 'none',
        border: 'none',
    },
    '& .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-sortIcon': {
        border: 'none',
        outline: 'none',
    },
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-root .MuiDataGrid-columnHeader:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-cell': {
        fontSize: '0.875rem',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'action.hover',
        cursor: 'pointer',
    },
    '& .MuiDataGrid-footerContainer': {
        fontSize: '0.875rem',
    },
});

type PodState = 'all' | 'free' | 'transferred' | 'assigned' | 'pending_transfer';

interface GetOrganizationPodsParams {
    serial_number?: string;
    simple_id?: string;
    athlete_name?: string;
    organization_name?: string;
    pod_state?: PodState;
    level?: number;
    created_at?: string;
    sort?:
        | 'serial_number'
        | 'created_at'
        | 'athlete_name'
        | '-serial_number'
        | '-created_at'
        | '-athlete_name';
    page?: number;
    per_page?: number;
}

interface OrganizationPod {
    uuid: string;
    serial_number: string;
    simple_id: string;
    athlete_name: string | null;
    transfer_request_sent_to: string | null;
    transfer_request_sent_at: Date | string | null;
    transfer_request_accepted_at: Date | string | null;
    subscription_id: string | null;
    created_at: Date | string;
}

interface Individual {
    name: string;
    uuid: string;
    image_urls: {
        avatar: string | null;
    };
    managing_user: Contact | null;
}

const getPodsForOrganization = (
    organizationUuid: string,
    options: GetOrganizationPodsParams,
): Promise<AxiosResponse<any>> => {
    return Request.get(`/organizations/${organizationUuid}/pods`, {
        params: {
            'filter[serial_number]': options.serial_number,
            'filter[simple_id]': options.simple_id,
            'filter[athlete_name]': options.athlete_name,
            'filter[organization_name]': options.organization_name,
            'filter[pod_state]': options.pod_state === 'all' ? undefined : options.pod_state,
            level: options.level,
            sort: options.sort,
            page: options.page,
            per_page: options.per_page,
        },
    });
};

const PodsPage = ({ organization }: { organization: Organization }) => {
    const [individuals, setIndividuals] = useState<Individual[]>([]);
    const [podTransferNameFilter, setPodTransferNameFilter] = useState<string>('');
    const [podAssignNameFilter, setPodAssignNameFilter] = useState<string>('');
    const [searchParams, setSearchParams] = useState<GetOrganizationPodsParams>({
        serial_number: '',
        simple_id: '',
        athlete_name: '',
        organization_name: '',
        pod_state: 'all',
        level: 0,
        sort: '-created_at',
    });
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [canTransferPod, setCanTransferPod] = useState(true);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 50,
    });
    const [totalRows, setTotalRows] = useState<number>(0);
    const [pods, setPods] = useState<OrganizationPod[]>([]);
    const [assignedCount, setAssignedCount] = useState<number>(0);
    const [availableCount, setAvailableCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(800));

    const openDialogModal = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMessage(message);
        setOpenDialog(true);
    };

    const closeDialogModal = () => {
        setOpenDialog(false);
    };

    const fetchPods = (options?: GetOrganizationPodsParams) => {
        setLoading(true);
        let requestParams = {
            ...searchParams,
            ...options,
            page: paginationModel.page + 1,
            per_page: paginationModel.pageSize,
        };
        console.debug('fetchPods params', requestParams);
        getPodsForOrganization(organization.uuid, requestParams)
            .then((response) => {
                setCanTransferPod(requestParams.level === 0);
                const updatedPods = response.data.data.map((pod: OrganizationPod) => {
                    const transferRequestAcceptedAt = pod.transfer_request_accepted_at
                        ? new Date(pod.transfer_request_accepted_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                          })
                        : '';
                    return {
                        ...pod,
                        transfer_request_accepted_at: transferRequestAcceptedAt,
                    };
                });
                setPods(updatedPods);
                setTotalRows(response.data.meta.total);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Failed to fetch pods', error);
                setLoading(false);
                openDialogModal(
                    'Error',
                    `Failed to fetch pods: ${
                        error.response?.data?.message || 'An error occurred'
                    }.`,
                );
            });
    };

    const fetchPodCounts = () => {
        // Fetch all pods to count assigned vs available
        // An assigned pod has a transfer_request_accepted_at date
        // An available pod has no transfer_request_accepted_at and no athlete_name
        // Use a large per_page to get accurate counts (most orgs won't have more than 1000 pods)
        getPodsForOrganization(organization.uuid, {
            ...searchParams,
            pod_state: 'all', // Get all pods to count properly
            page: 1,
            per_page: 1000, // Fetch up to 1000 pods for counting
        })
            .then((response) => {
                const allPods = response.data.data as OrganizationPod[];

                // Debug: Log sample pods to see actual data structure
                console.log('Total pods fetched for counting:', allPods.length);
                if (allPods.length > 0) {
                    const samplePods = allPods.slice(0, 3);
                    console.log(
                        'Sample pods for counting:',
                        samplePods.map((pod) => ({
                            serial_number: pod.serial_number,
                            transfer_request_accepted_at: pod.transfer_request_accepted_at,
                            transfer_request_accepted_at_type:
                                typeof pod.transfer_request_accepted_at,
                            athlete_name: pod.athlete_name,
                            subscription_id: pod.subscription_id,
                        })),
                    );
                }

                // Count assigned pods (those with transfer_request_accepted_at)
                // An assigned pod has a transfer_request_accepted_at date
                // The API returns this as a string (ISO date) or Date object
                const assigned = allPods.filter((pod) => {
                    const acceptedAt = pod.transfer_request_accepted_at as
                        | Date
                        | string
                        | null
                        | undefined;

                    // If it's null, undefined, or empty string, it's not assigned
                    if (!acceptedAt) return false;
                    if (acceptedAt === null || acceptedAt === undefined) return false;
                    if (typeof acceptedAt === 'string' && acceptedAt.trim() === '') return false;

                    // Try to parse as Date to see if it's a valid date
                    try {
                        const date = acceptedAt instanceof Date ? acceptedAt : new Date(acceptedAt);
                        // Check if it's a valid date
                        if (isNaN(date.getTime())) return false;
                        // If we got here, it's a valid date, so the pod is assigned
                        return true;
                    } catch (e) {
                        // If parsing fails, it's not a valid date
                        return false;
                    }
                }).length;

                // Count available pods (those without transfer_request_accepted_at and without athlete_name)
                const available = allPods.filter((pod) => {
                    const acceptedAt = pod.transfer_request_accepted_at as
                        | Date
                        | string
                        | null
                        | undefined;
                    const hasAcceptedDate =
                        acceptedAt !== null &&
                        acceptedAt !== undefined &&
                        (typeof acceptedAt === 'string'
                            ? acceptedAt !== '' && acceptedAt.trim() !== ''
                            : true);
                    const hasAthlete =
                        pod.athlete_name && pod.athlete_name !== null && pod.athlete_name !== '';
                    return !hasAcceptedDate && !hasAthlete;
                }).length;

                console.log('Pod counts calculated:', {
                    assigned,
                    available,
                    total: allPods.length,
                    assignedPods: allPods
                        .filter((pod) => {
                            const acceptedAt = pod.transfer_request_accepted_at as
                                | Date
                                | string
                                | null
                                | undefined;
                            if (!acceptedAt) return false;
                            if (acceptedAt === null || acceptedAt === undefined) return false;
                            if (typeof acceptedAt === 'string' && acceptedAt.trim() === '')
                                return false;
                            try {
                                const date =
                                    acceptedAt instanceof Date ? acceptedAt : new Date(acceptedAt);
                                return !isNaN(date.getTime());
                            } catch (e) {
                                return false;
                            }
                        })
                        .map((p) => p.serial_number),
                });

                setAssignedCount(assigned);
                setAvailableCount(available);
            })
            .catch((error) => {
                console.error('Failed to fetch pod counts', error);
                // If fetch fails, set counts to 0 to avoid showing incorrect data
                setAssignedCount(0);
                setAvailableCount(0);
            });
    };

    const fetchIndividuals = () => {
        getIndividuals({
            'filter[organization]': organization.uuid,
            'filter[name]': podTransferNameFilter,
            'filter[has_managing_user]': true,
        })
            .then((response) => {
                setIndividuals(response.data.data);
            })
            .catch((error) => {
                console.error('Failed to fetch individuals', error);
            });
    };

    const debouncedSetPodTransferNameFilter = debounce((newInputValue) => {
        setPodTransferNameFilter(newInputValue);
    }, 300);

    const debouncedSetPodAssignNameFilter = debounce((newInputValue) => {
        setPodAssignNameFilter(newInputValue);
    }, 300);

    useEffect(() => {
        fetchPods();
        fetchPodCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, searchParams]);

    useEffect(() => {
        fetchIndividuals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [podTransferNameFilter]);

    useEffect(() => {
        fetchIndividuals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [podAssignNameFilter]);

    const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
        setPaginationModel(newPaginationModel);
    };

    const [openConfirmTransferDialog, setOpenConfirmTransferDialog] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [openConfirmAssignDialog, setOpenConfirmAssignDialog] = useState(false);
    const [openConfirmCancelPendingTransferDialog, setOpenConfirmCancelPendingTransferDialog] =
        useState(false);
    const [selectedPod, setSelectedPod] = useState<OrganizationPod | null>(null);
    const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);

    const handleTransferClick = useCallback((pod: OrganizationPod) => {
        setSelectedPod(pod);
        setOpenConfirmTransferDialog(true);
    }, []);

    const handleAssignClick = useCallback((pod: OrganizationPod) => {
        setSelectedPod(pod);
        setOpenConfirmAssignDialog(true);
    }, []);

    const handleCancelPendingTransferClick = useCallback((pod: OrganizationPod) => {
        setSelectedPod(pod);
        setOpenConfirmCancelPendingTransferDialog(true);
    }, []);

    const CustomNoRowsOverlay = () => (
        <GridOverlay>
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary">
                    No pods found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {searchParams.pod_state !== 'all' || searchParams.level !== 0
                        ? 'Try adjusting your filters to see more results.'
                        : 'No pods are available for this organization.'}
                </Typography>
            </Stack>
        </GridOverlay>
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'organization_name',
                headerName: 'Organization',
                minWidth: 120,
                flex: 0.8,
                sortable: true,
            },
            {
                field: 'serial_number',
                headerName: 'Serial Number',
                minWidth: 180,
                flex: 1,
                sortable: true,
            },
            {
                field: 'simple_id',
                headerName: 'Simple ID',
                minWidth: 100,
                flex: 0.6,
                sortable: true,
            },
            {
                field: 'athlete_name',
                headerName: 'Athlete',
                minWidth: 150,
                flex: 1,
                sortable: true,
            },
            {
                field: 'transfer_info',
                headerName: 'Request Sent To',
                minWidth: 200,
                flex: 1.2,
                sortable: false,
                renderCell: (params: { row: OrganizationPod }) => {
                    const transferTo = params.row.transfer_request_sent_to;
                    const transferAt = params.row.transfer_request_sent_at
                        ? new Date(params.row.transfer_request_sent_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                          })
                        : '';
                    return transferTo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {transferTo}
                            </Typography>
                            <Chip
                                label={transferAt}
                                color="primary"
                                size="small"
                                sx={{
                                    fontSize: '0.75rem',
                                    height: '20px',
                                }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    );
                },
            },
            {
                field: 'transfer_request_accepted_at',
                headerName: 'Request Accepted At',
                minWidth: 220,
                flex: 1.2,
                sortable: true,
                renderCell: (params: { row: OrganizationPod }) => {
                    const acceptedAt = params.row.transfer_request_accepted_at
                        ? new Date(params.row.transfer_request_accepted_at).toLocaleString(
                              'en-US',
                              {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                              },
                          )
                        : '';
                    const assignOrTransfer = params.row.subscription_id
                        ? 'Transferred'
                        : 'Assigned';
                    const hasSubscription = !!params.row.subscription_id;

                    if (!acceptedAt) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                                —
                            </Typography>
                        );
                    }

                    const chipIcon = hasSubscription ? (
                        <CheckIcon sx={{ color: 'white', fontSize: 14 }} />
                    ) : undefined;

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {acceptedAt}
                            </Typography>
                            <Chip
                                icon={chipIcon}
                                label={assignOrTransfer}
                                size="small"
                                sx={{
                                    fontSize: '0.75rem',
                                    height: '20px',
                                    backgroundColor: hasSubscription
                                        ? theme.palette.success.main
                                        : theme.palette.grey[300],
                                    color: hasSubscription ? 'white' : 'inherit',
                                    '& .MuiChip-icon': {
                                        color: 'white',
                                    },
                                }}
                            />
                        </Box>
                    );
                },
            },
            {
                field: 'created_at',
                headerName: 'Created At',
                minWidth: 150,
                flex: 0.8,
                sortable: true,
                valueFormatter: ((params: number) => {
                    return params ? new Date(params).toLocaleDateString('en-US') : '';
                }) as GridValueFormatter<Pod>,
            },
            {
                field: 'transfer',
                headerName: 'Actions',
                minWidth: 300,
                flex: 1.5,
                sortable: false,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: { row: OrganizationPod }) =>
                    params.row.transfer_request_sent_to &&
                    !params.row.transfer_request_accepted_at ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleCancelPendingTransferClick(params.row)}
                            sx={{ fontSize: '0.8rem', textTransform: 'none' }}
                        >
                            Cancel Transfer
                        </Button>
                    ) : !params.row.subscription_id && canTransferPod ? (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleTransferClick(params.row)}
                                sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                            >
                                Transfer
                                <PersonIcon />
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleAssignClick(params.row)}
                                sx={{
                                    fontSize: '0.7rem',
                                    textTransform: 'none',
                                    marginLeft: '4px',
                                }}
                            >
                                Assign
                                <PersonIcon />
                            </Button>
                        </>
                    ) : null,
            },
        ],
        [
            canTransferPod,
            handleCancelPendingTransferClick,
            handleTransferClick,
            handleAssignClick,
            theme,
        ],
    );

    const handleCancelTransferClick = () => {
        setOpenConfirmTransferDialog(false);
        setPodTransferNameFilter('');
        setSelectedIndividual(null);
    };

    const handleCancelAssignClick = () => {
        setOpenConfirmAssignDialog(false);
        setPodAssignNameFilter('');
        setSelectedIndividual(null);
    };

    const handleConfirmTransferClick = () => {
        if (selectedIndividual && selectedPod) {
            setIsTransferring(true);
            requestPodTransferToAthlete(organization.uuid, selectedPod.uuid, {
                individual_uuid: selectedIndividual.uuid,
            })
                .then((response: AxiosResponse) => {
                    console.debug('Transfer request successful', response);
                    setOpenConfirmTransferDialog(false);
                    setPodTransferNameFilter('');
                    setSelectedIndividual(null);
                    openDialogModal('Success', response.data.message);
                    fetchPods();
                    fetchPodCounts();
                })
                .catch((error: any) => {
                    setOpenConfirmTransferDialog(false);
                    console.error('Failed to request transfer', error);
                    let errorMsg = error.response?.data?.message || 'an unknown error occurred.';
                    openDialogModal('Error', `Failed to request transfer: ${errorMsg}`);
                })
                .finally(() => {
                    setIsTransferring(false);
                });
        }
    };

    const handleConfirmAssignClick = () => {
        if (selectedIndividual && selectedPod) {
            assignPodToAthlete(organization.uuid, selectedPod.uuid, {
                individual_uuid: selectedIndividual.uuid,
            })
                .then((response: AxiosResponse) => {
                    console.debug('Assignment successful', response);
                    setOpenConfirmAssignDialog(false);
                    setPodAssignNameFilter('');
                    setSelectedIndividual(null);
                    openDialogModal('Success', 'Successfully assigned athlete to pod');
                    fetchPods();
                    fetchPodCounts();
                })
                .catch((error: any) => {
                    setOpenConfirmAssignDialog(false);
                    console.error('Failed to assign athlete', error);
                    let errorMsg = error.response?.data?.message || 'an unknown error occurred.';
                    openDialogModal('Error', `Failed to assign athlete: ${errorMsg}`);
                });
        }
    };

    const handleRemoveAthleteClick = () => {
        if (selectedPod) {
            removeAthleteFromPod(organization.uuid, selectedPod.uuid)
                .then(() => {
                    console.debug('Remove athlete successful');
                    setOpenConfirmAssignDialog(false);
                    setPodAssignNameFilter('');
                    setSelectedIndividual(null);
                    openDialogModal('Success', 'Successfully removed athlete from pod');
                    fetchPods();
                    fetchPodCounts();
                })
                .catch((error: any) => {
                    setOpenConfirmAssignDialog(false);
                    console.error('Failed to remove athlete', error);
                    let errorMsg = error.response?.data?.message || 'an unknown error occurred.';
                    openDialogModal('Error', `Failed to remove athlete: ${errorMsg}`);
                });
        }
    };

    const handleConfirmCancelPendingTransferClick = () => {
        if (selectedPod) {
            cancelPodTransferToAthlete(organization.uuid, selectedPod.uuid)
                .then((response: AxiosResponse) => {
                    console.debug('Cancel pod transfer request successful', response);
                    setOpenConfirmCancelPendingTransferDialog(false);
                    openDialogModal('Success', response.data.message);
                    fetchPods();
                    fetchPodCounts();
                })
                .catch((error: any) => {
                    setOpenConfirmCancelPendingTransferDialog(false);
                    console.error('Failed to cancel pod transfer', error);
                    let errorMsg = error.response?.data?.message || 'an unknown error occurred.';
                    openDialogModal('Error', `Failed to cancel pod transfer: ${errorMsg}`);
                });
        }
    };

    const handleSearchFieldChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setSearchParams({
            ...searchParams,
            [event.target.name as string]: event.target.value,
        });
    };

    const handleSort = (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        /**
         * Sort will be undefined, or it will be ascending, ie "createdAt", or in this case
         * it will be descending, ie "-createdAt". If descending, we prepend a hyphen.
         */
        if (sortField && sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }
        let updatedSearchParams = {
            ...searchParams,
            sort: sortField as GetOrganizationPodsParams['sort'],
        };
        setSearchParams(updatedSearchParams);
        fetchPods(updatedSearchParams);
    };

    return (
        <>
            <Paper sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        marginY: 4,
                        marginX: 10,
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: isSmallScreen ? 'column' : 'row',
                        flexWrap: 'wrap',
                    }}
                >
                    <Box
                        sx={{
                            marginRight: 1,
                            spacing: 1,
                            flexDirection: isSmallScreen ? 'column' : 'row',
                            flexWrap: 'wrap',
                            display: 'flex',
                        }}
                    >
                        <FormControl
                            size="small"
                            sx={{ width: '240px', display: 'inline-flex', marginRight: '2px' }}
                        >
                            <InputLabel id="order-status-label">Downline Level</InputLabel>
                            <Select
                                label="Downline Level"
                                value={searchParams.level}
                                onChange={(e) => {
                                    const newLevel = e.target.value as number;
                                    setSearchParams((prev) => ({
                                        ...prev,
                                        level: newLevel,
                                    }));
                                }}
                                size="small"
                                sx={{ width: '240px', display: 'inline-flex', marginRight: '2px' }}
                            >
                                <MenuItem value={0}>My Pods</MenuItem>
                                <MenuItem value={1}>Level 1 Pods</MenuItem>
                                <MenuItem value={2}>Level 2 Pods</MenuItem>
                                <MenuItem value={3}>Level 3 Pods</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{
                                width: '180px',
                                display: 'inline-flex',
                                marginLeft: '2px',
                                marginRight: '2px',
                            }}
                        >
                            <InputLabel id="pod-state-label">Pod State</InputLabel>
                            <Select
                                labelId="pod-state-label"
                                label="Pod State"
                                value={searchParams.pod_state}
                                onChange={(e) => {
                                    const newPodState = e.target.value as PodState;
                                    setSearchParams((prev) => ({
                                        ...prev,
                                        pod_state: newPodState,
                                    }));
                                }}
                                size="small"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="free">Free</MenuItem>
                                <MenuItem value="transferred">Transferred</MenuItem>
                                <MenuItem value="assigned">Assigned</MenuItem>
                                <MenuItem value="pending_transfer">Pending Transfer</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            name="serial_number"
                            value={searchParams.serial_number}
                            onChange={handleSearchFieldChange}
                            placeholder="Enter Serial Number"
                            label="Serial Number"
                            size={'small'}
                            style={{ margin: '2px' }}
                        />
                        <TextField
                            name="simple_id"
                            value={searchParams.simple_id}
                            onChange={handleSearchFieldChange}
                            placeholder="Enter Simple ID"
                            label="Simple ID"
                            size={'small'}
                            style={{ margin: '2px' }}
                        />
                        <TextField
                            name="athlete_name"
                            value={searchParams.athlete_name}
                            onChange={handleSearchFieldChange}
                            placeholder="Enter Athlete Name"
                            label="Athlete Name"
                            size={'small'}
                            style={{ margin: '2px' }}
                        />
                        <TextField
                            name="organization_name"
                            value={searchParams.organization_name}
                            onChange={handleSearchFieldChange}
                            placeholder="Enter Organization Name"
                            label="Organization Name"
                            size={'small'}
                            style={{ margin: '2px' }}
                        />
                    </Box>
                    <Box>
                        <Button
                            type="submit"
                            onClick={() => {
                                fetchPods();
                                fetchPodCounts();
                            }}
                            variant="contained"
                            sx={{
                                fontSize: '0.7rem',
                                height: '2.5rem',
                            }}
                        >
                            Search Pods
                        </Button>
                    </Box>
                </Box>
                <Box
                    sx={{
                        marginX: 10,
                        marginBottom: 3,
                    }}
                >
                    <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
                        {/* Total Pods Card */}
                        <Paper
                            elevation={4}
                            sx={{
                                flex: 1,
                                padding: 3,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}08 100%)`,
                                border: `2px solid ${theme.palette.primary.main}30`,
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6,
                                    borderColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        borderRadius: 2,
                                        padding: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <InventoryIcon sx={{ color: 'white', fontSize: 28 }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Total Pods
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.palette.primary.main,
                                            lineHeight: 1.2,
                                            mt: 0.5,
                                        }}
                                    >
                                        {totalRows}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Assigned Pods Card */}
                        <Paper
                            elevation={4}
                            sx={{
                                flex: 1,
                                padding: 3,
                                background: `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.dark}08 100%)`,
                                border: `2px solid ${theme.palette.success.main}30`,
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6,
                                    borderColor: theme.palette.success.main,
                                },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.success.main,
                                        borderRadius: 2,
                                        padding: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <HowToRegIcon sx={{ color: 'white', fontSize: 28 }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Assigned Pods
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.palette.success.main,
                                            lineHeight: 1.2,
                                            mt: 0.5,
                                        }}
                                    >
                                        {assignedCount}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Available Pods Card */}
                        <Paper
                            elevation={4}
                            sx={{
                                flex: 1,
                                padding: 3,
                                background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.dark}08 100%)`,
                                border: `2px solid ${theme.palette.info.main}30`,
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6,
                                    borderColor: theme.palette.info.main,
                                },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.info.main,
                                        borderRadius: 2,
                                        padding: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Inventory2Icon sx={{ color: 'white', fontSize: 28 }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Available Pods
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.palette.info.main,
                                            lineHeight: 1.2,
                                            mt: 0.5,
                                        }}
                                    >
                                        {availableCount}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        height: 'calc(100vh - 500px)',
                        minHeight: '500px',
                    }}
                >
                    <StyledDataGrid
                        rows={pods}
                        columns={columns}
                        checkboxSelection={false}
                        sortingMode="server"
                        onSortModelChange={(model) => handleSort(model)}
                        getRowId={(row) => row.serial_number}
                        pagination
                        paginationMode="server"
                        rowCount={totalRows}
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        pageSizeOptions={[25, 50, 75, 100]}
                        loading={loading}
                        disableRowSelectionOnClick
                        density="compact"
                        autoHeight={false}
                        initialState={{
                            pinnedColumns: { left: ['serial_number'] },
                            sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
                        }}
                        sx={{
                            backgroundColor: '#ffffff',
                            boxShadow: 3,
                            borderRadius: 2,
                            '& .MuiDataGrid-virtualScrollerContent': {
                                height: '100%!important',
                            },
                        }}
                        slots={{
                            loadingOverlay: () => (
                                <GridOverlay>
                                    <Stack
                                        spacing={2}
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{ py: 4 }}
                                    >
                                        <Typography variant="body1">Loading pods...</Typography>
                                    </Stack>
                                </GridOverlay>
                            ),
                            noRowsOverlay: CustomNoRowsOverlay,
                        }}
                    />
                </Box>
            </Paper>
            <Dialog
                open={openConfirmTransferDialog}
                onClose={() => setOpenConfirmTransferDialog(false)}
            >
                <DialogTitle>Transfer Pod to Athlete</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        id="individuals-select"
                        options={individuals}
                        getOptionLabel={(option) => option.name}
                        value={selectedIndividual}
                        onInputChange={(event, newInputValue, reason) => {
                            if (reason === 'input') {
                                debouncedSetPodTransferNameFilter(newInputValue);
                            }
                        }}
                        onChange={(event, newValue) => {
                            setSelectedIndividual(newValue);
                        }}
                        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                key={option.uuid}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                {option.image_urls.avatar ? (
                                    <img
                                        src={option.image_urls.avatar}
                                        alt={option.name}
                                        style={{
                                            marginRight: 8,
                                            width: 24,
                                            height: 24,
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            marginRight: 8,
                                            width: 24,
                                            height: 24,
                                        }}
                                    ></div>
                                )}
                                {option.name} ({option.managing_user?.user_name})
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} variant="standard" label="Select Individual" />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelTransferClick}
                        sx={{
                            color: 'primary.main',
                            backgroundColor: '#fff',
                            borderColor: 'primary.main',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmTransferClick}
                        disabled={isTransferring}
                        color="primary"
                        sx={{
                            backgroundColor: 'primary.main',
                            color: '#fff',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: '#fff',
                            },
                        }}
                    >
                        {isTransferring ? 'Transferring...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openConfirmAssignDialog}
                onClose={() => setOpenConfirmAssignDialog(false)}
            >
                <DialogTitle>Assign Pod to Athlete</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        id="individuals-select"
                        options={individuals}
                        getOptionLabel={(option) => option.name}
                        value={selectedIndividual}
                        onInputChange={(event, newInputValue, reason) => {
                            if (reason === 'input') {
                                debouncedSetPodAssignNameFilter(newInputValue);
                            }
                        }}
                        onChange={(event, newValue) => {
                            setSelectedIndividual(newValue);
                        }}
                        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                key={option.uuid}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                {option.image_urls.avatar ? (
                                    <img
                                        src={option.image_urls.avatar}
                                        alt={option.name}
                                        style={{
                                            marginRight: 8,
                                            width: 24,
                                            height: 24,
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            marginRight: 8,
                                            width: 24,
                                            height: 24,
                                        }}
                                    ></div>
                                )}
                                {option.name} ({option.managing_user?.user_name})
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} variant="standard" label="Select Individual" />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleRemoveAthleteClick}
                        disabled={!selectedPod?.athlete_name}
                        size="small"
                        sx={{
                            color: '#fff',
                            backgroundColor: 'error.main',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'error.dark',
                            },
                            '&:disabled': {
                                backgroundColor: 'grey.300',
                                color: 'grey.500',
                            },
                        }}
                    >
                        Remove Athlete
                    </Button>
                    <Button
                        onClick={handleConfirmAssignClick}
                        color="primary"
                        size="small"
                        sx={{
                            backgroundColor: 'primary.main',
                            color: '#fff',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: '#fff',
                            },
                        }}
                    >
                        Assign
                    </Button>
                    <Button
                        onClick={handleCancelAssignClick}
                        size="small"
                        sx={{
                            color: 'primary.main',
                            backgroundColor: '#fff',
                            borderColor: 'primary.main',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openConfirmCancelPendingTransferDialog}
                onClose={() => setOpenConfirmCancelPendingTransferDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <Paper style={{ minWidth: '300px' }}>
                    <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            This will cancel this pod transfer. You can then transfer this pod to
                            another athlete.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setOpenConfirmCancelPendingTransferDialog(false)}
                            variant="outlined"
                            autoFocus
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleConfirmCancelPendingTransferClick}
                            variant="contained"
                            autoFocus
                        >
                            Cancel Transfer
                        </Button>
                    </DialogActions>
                </Paper>
            </Dialog>
            <Dialog
                open={openDialog}
                onClose={closeDialogModal}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <Paper style={{ minWidth: '300px' }}>
                    <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {dialogMessage}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialogModal} variant="contained" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Paper>
            </Dialog>
        </>
    );
};
export default PodsPage;
